import { useEffect, useRef, useState } from 'react';
import { TrendingDown, Users, Zap, AlertTriangle } from 'lucide-react';

const problems = [
  {
    icon: TrendingDown,
    stat: '$0',
    statLabel: 'Revenue From Zelle',
    title: 'You\'re Funding Your Competitors',
    description: 'Your bank processes billions in transactions through Zelle and earns zero revenue — while paying licensing fees. You\'re subsidizing the very companies stealing your customers.',
    color: '#EF4444',
    accent: 'rgba(239,68,68,0.1)',
    border: 'rgba(239,68,68,0.3)',
  },
  {
    icon: Users,
    stat: '40M+',
    statLabel: 'Americans Left Behind',
    title: 'A Massive Market You\'re Missing',
    description: '40 million unbanked Americans pay 4–8% per transaction to check cashers. Your bank could serve them profitably — but lacks the digital infrastructure to reach them.',
    color: '#F59E0B',
    accent: 'rgba(245,158,11,0.1)',
    border: 'rgba(245,158,11,0.3)',
  },
  {
    icon: Zap,
    stat: '82%+',
    statLabel: 'Banks Not on FedNow',
    title: 'The Window Is Open. For Now.',
    description: '82%+ of U.S. banks remain unconnected to FedNow as of Q2 2026. Real-time payment volume is on a 30%+ CAGR — 8 billion transactions in 2026, projected 13.9 billion by 2028. First movers capture the market.',
    color: '#8B5CF6',
    accent: 'rgba(139,92,246,0.1)',
    border: 'rgba(139,92,246,0.3)',
  },
];

export default function ProblemSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setRevealed(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="problem"
      data-testid="problem-section"
      ref={sectionRef}
      className="relative section-padding bg-black"
      style={{ scrollMarginTop: '80px' }}
    >
      {/* Subtle red glow background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 opacity-5"
        style={{ background: 'radial-gradient(circle, #EF4444, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-3xl mb-16"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(30px)',
            transition: 'all 0.7s ease',
          }}>
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle size={18} color="#EF4444" />
            <span className="text-[#EF4444] font-mono text-xs uppercase tracking-widest">The Problem</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black uppercase text-white mb-4 leading-tight"
            style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Your Bank Is Funding
            <span className="text-[#EF4444]"> Its Own Competition</span>
          </h2>
          <p className="text-[#A3A3A3] text-base leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Every day community banks rely on third-party fintechs, they bleed revenue, brand equity, and customer relationships. The window to act is closing.
          </p>
        </div>

        {/* Problem Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {problems.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`problem-card-${i}`}
                style={{
                  background: '#0A0A0A',
                  border: `1px solid ${p.border}`,
                  padding: '40px 32px',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: revealed ? 1 : 0,
                  transform: revealed ? 'translateY(0)' : 'translateY(40px)',
                  transition: `all 0.7s ease ${0.2 + i * 0.15}s`,
                }}
                className="group hover:-translate-y-2 hover:shadow-lg"
              >
                {/* Glow accent */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-20 rounded-full"
                  style={{ background: p.color, filter: 'blur(40px)', transform: 'translate(30%, -30%)' }} />

                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className="p-3 rounded" style={{ background: p.accent, border: `1px solid ${p.border}` }}>
                      <Icon size={24} color={p.color} />
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-5xl font-black font-mono mb-1" style={{ color: p.color, fontFamily: 'JetBrains Mono, monospace' }}>
                      {p.stat}
                    </div>
                    <div className="text-xs uppercase tracking-widest font-mono" style={{ color: p.color, opacity: 0.7 }}>
                      {p.statLabel}
                    </div>
                  </div>

                  <h3 className="text-white font-bold text-lg mb-3 uppercase tracking-tight"
                    style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '14px', lineHeight: '1.4' }}>
                    {p.title}
                  </h3>
                  <p className="text-[#A3A3A3] text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {p.description}
                  </p>
                </div>

                {/* Bottom line */}
                <div className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: p.color }} />
              </div>
            );
          })}
        </div>

        {/* Bottom callout */}
        <div
          className="mt-12 border border-[#EF4444] border-opacity-30 p-6 flex flex-col md:flex-row items-center justify-between gap-4"
          style={{
            background: 'rgba(239,68,68,0.05)',
            opacity: revealed ? 1 : 0,
            transform: revealed ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s ease 0.7s',
          }}
        >
          <div className="flex items-center gap-4">
            <AlertTriangle size={20} color="#EF4444" className="flex-shrink-0" />
            <p className="text-white text-sm font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-[#EF4444] font-bold">The cost of inaction:</span> Every year without a sovereign payment layer costs your institution millions in lost non-interest income, eroded brand equity, and ceded customer relationships.
            </p>
          </div>
          <button
            onClick={() => document.querySelector('#solution')?.scrollIntoView({ behavior: 'smooth' })}
            data-testid="problem-see-solution-btn"
            className="flex-shrink-0 btn-neon text-xs"
            style={{ fontFamily: 'Unbounded, sans-serif', padding: '12px 24px' }}
          >
            See the Solution
          </button>
        </div>
      </div>
    </section>
  );
}
